import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { PageIntro, Section } from '@/components/PageLayout'

const commitments = [
  {
    title: 'Capital protection',
    body: 'Every acquisition must hold up in the downside case. The firm does not ask investors to fund a property its founder would not invest in himself.',
  },
  {
    title: 'Reporting',
    body: 'Quarterly reports cover property-level financials, occupancy, capital work, and local market conditions.',
  },
  {
    title: 'Hold period',
    body: 'The portfolio is built to be held across market cycles, measured in years rather than quarters.',
  },
]

export default function AboutPage() {
  return (
    <div className="bg-background text-foreground">
      <PageIntro title="About Fairmeadow Capital">
        <p>
          Fairmeadow Capital was founded in 2025 by Garrett John Law to acquire and operate residential real estate on
          behalf of investors.
        </p>
      </PageIntro>

      <Section title="Founder">
        <div className="grid gap-8 sm:grid-cols-[10rem_1fr] sm:gap-10">
          <img
            src="/headshot.webp"
            alt="Garrett John Law"
            width={640}
            height={640}
            className="aspect-square w-32 object-cover sm:w-40"
          />
          <div className="text-fluid-body-lg max-w-2xl space-y-5">
            <p>
              I&rsquo;m a software engineer by profession. For years I watched colleagues earn good incomes and still
              struggle to build wealth outside the stock market. They wanted exposure to real estate but didn&rsquo;t
              have the time or the knowledge to buy and run property themselves.
            </p>
            <p>
              Real estate appealed to me because it can be verified: you can walk the building, confirm the rents, and
              underwrite the expenses. When I compared markets, the popular ones offered high prices and thin yields.
              Cleveland offered stable demand, affordable properties, and rents that cover costs, in a market I could
              learn in depth. I bought the firm&rsquo;s first property, a duplex in the West Boulevard neighborhood, in
              April 2025.
            </p>
            <p>
              Fairmeadow Capital pairs the time and analysis I can put into each deal with investors who have capital
              but not the time to operate property. I named it for the kind of growth I&rsquo;m after: steady, like a
              meadow returning every spring.
            </p>
          </div>
        </div>
      </Section>

      <section className="bg-navy text-white">
        <figure className="mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-24">
          <blockquote className="max-w-3xl font-serif text-[clamp(1.5rem,1.6vw+1rem,2.25rem)] leading-snug">
            I&rsquo;m not trying to build the biggest real estate firm. I&rsquo;m trying to build one that serves its
            investors well, treats its residents fairly, and contributes to the neighborhoods where it owns property.
          </blockquote>
          <figcaption className="mt-6 text-sm text-[#c5ced8]">Garrett John Law, founder</figcaption>
        </figure>
      </section>

      <Section title="Commitments">
        <dl className="max-w-2xl space-y-7">
          {commitments.map((c) => (
            <div key={c.title}>
              <dt className="text-navy mb-1.5 font-semibold">{c.title}</dt>
              <dd className="text-foreground/85 leading-relaxed">{c.body}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section title="Outside the firm">
        <p className="text-foreground/85 max-w-2xl leading-relaxed">
          Garrett is also a professional classical pianist and directs a music festival in Hungary. More about his work
          in software and music is at{' '}
          <a href="https://garrettjohnlaw.com" target="_blank" rel="noopener noreferrer" className="prose-link">
            garrettjohnlaw.com
          </a>
          .
        </p>
      </Section>

      <section className="bg-muted">
        <div className="mx-auto flex max-w-5xl flex-col gap-6 px-5 py-14 md:flex-row md:items-center md:justify-between md:px-8">
          <p className="text-fluid-h3 text-navy max-w-xl">How investors participate in each property.</p>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/invest">Investing with Fairmeadow</Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
